/**
 * Voice allocation (audio-direction §2): a fixed pool of one-shot voice slots (48 in total, minus the voices
 * the persistent loops reserve), a cap per sound, and the priority order
 *
 *   own fire (0) > incoming hits (1) > locks and alarms (2) > nearby (3) > distant (4).
 *
 * A play past its sound's cap steals that sound's oldest voice of the same or lower priority. A play into a
 * full pool takes a free slot if one has ended, else steals the least important voice (lowest priority, then
 * oldest) as long as that voice is not more important than the new one; otherwise the new play is dropped.
 * Pure bookkeeping on preallocated typed arrays (D-014): the engine owns the Web Audio nodes behind each slot.
 */

/** Priority classes, most important first. */
export const PRIORITY = Object.freeze({ ownFire: 0, incoming: 1, alarm: 2, near: 3, distant: 4 })
export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY]

/** What the last `acquireVoice` did. */
export const ACQUIRE = Object.freeze({ free: 0, stoleSame: 1, stoleLower: 2, dropped: 3 })
export type AcquireResult = (typeof ACQUIRE)[keyof typeof ACQUIRE]

export interface VoicePool {
  readonly size: number
  /** Per sound: simultaneous voice cap. */
  readonly caps: Int32Array
  /** Per slot: the sound playing (-1 = never used). */
  readonly sound: Int32Array
  readonly priority: Uint8Array
  /** Per slot: start and end time (s); the slot is free once `now >= end`. */
  readonly start: Float64Array
  readonly end: Float64Array
  /** What the last acquire did, and the voice it replaced (sound, -1 if none). */
  result: AcquireResult
  replacedSound: number
}

export function createVoicePool(size: number, caps: readonly number[]): VoicePool {
  if (!Number.isInteger(size) || size < 1) throw new Error(`voice pool size must be a positive integer (got ${size})`)
  const c = new Int32Array(caps.length)
  for (let i = 0; i < caps.length; i++) {
    const n = caps[i]
    if (!Number.isInteger(n) || n < 1) throw new Error(`voice cap of sound ${i} must be a positive integer (got ${n})`)
    c[i] = n
  }
  return {
    size,
    caps: c,
    sound: new Int32Array(size).fill(-1),
    priority: new Uint8Array(size),
    start: new Float64Array(size),
    end: new Float64Array(size),
    result: ACQUIRE.free,
    replacedSound: -1,
  }
}

function claim(pool: VoicePool, slot: number, sound: number, priority: Priority, now: number, duration: number, result: AcquireResult): number {
  pool.result = result
  pool.replacedSound = result === ACQUIRE.free ? -1 : pool.sound[slot]
  pool.sound[slot] = sound
  pool.priority[slot] = priority
  pool.start[slot] = now
  pool.end[slot] = now + duration
  return slot
}

/**
 * Claims a slot for `sound` at `priority`, starting `now` for `duration` seconds. Returns the slot, or -1 when
 * the play is dropped (`pool.result` says which case happened).
 */
export function acquireVoice(pool: VoicePool, sound: number, priority: Priority, now: number, duration: number): number {
  // 1. The per-sound cap: steal this sound's oldest voice that is not more important than the new play.
  let active = 0
  let oldestSame = -1
  for (let i = 0; i < pool.size; i++) {
    if (pool.sound[i] !== sound || now >= pool.end[i]) continue
    active++
    if (pool.priority[i] >= priority && (oldestSame < 0 || pool.start[i] < pool.start[oldestSame])) oldestSame = i
  }
  if (active >= pool.caps[sound]) {
    if (oldestSame < 0) {
      pool.result = ACQUIRE.dropped
      pool.replacedSound = -1
      return -1
    }
    return claim(pool, oldestSame, sound, priority, now, duration, ACQUIRE.stoleSame)
  }
  // 2. A free slot; else 3. the least important, oldest voice, if it is not more important than the new play.
  let victim = -1
  for (let i = 0; i < pool.size; i++) {
    if (now >= pool.end[i]) return claim(pool, i, sound, priority, now, duration, ACQUIRE.free)
    if (victim < 0 || pool.priority[i] > pool.priority[victim] || (pool.priority[i] === pool.priority[victim] && pool.start[i] < pool.start[victim])) victim = i
  }
  if (victim < 0 || pool.priority[victim] < priority) {
    pool.result = ACQUIRE.dropped
    pool.replacedSound = -1
    return -1
  }
  return claim(pool, victim, sound, priority, now, duration, ACQUIRE.stoleLower)
}

/** Frees a slot now. */
export function releaseVoice(pool: VoicePool, slot: number): void {
  pool.end[slot] = 0
}

/** Voices playing at `now` (of `sound`, or of every sound when `sound` is -1). */
export function activeVoices(pool: VoicePool, now: number, sound = -1): number {
  let n = 0
  for (let i = 0; i < pool.size; i++) if (now < pool.end[i] && (sound < 0 || pool.sound[i] === sound)) n++
  return n
}
